import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createInventoryItem,
  deleteInventoryItem,
  linkInventoryToGlobal,
  listInventory,
  updateInventoryItem,
} from '@/api/doer'
import type {
  CreateInventoryItemRequest,
  LinkInventoryRequest,
  UpdateInventoryItemRequest,
} from '@/api/types'
import { FOLLOWINGS_QUERY_KEY } from './use-doer'

export const INVENTORY_QUERY_KEY = ['inventory'] as const

export const useInventory = () =>
  useQuery({
    queryKey: [...INVENTORY_QUERY_KEY, 'list'],
    queryFn: listInventory,
  })

const invalidateInventoryAndFollowings = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: INVENTORY_QUERY_KEY })
  // Personalized BOM is computed client-side from the cached following + inventory, so the
  // following detail also needs to refetch its inventory snapshot.
  queryClient.invalidateQueries({ queryKey: FOLLOWINGS_QUERY_KEY })
}

export const useCreateInventoryItem = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateInventoryItemRequest) => createInventoryItem(data),
    onSuccess: () => invalidateInventoryAndFollowings(queryClient),
  })
}

export const useUpdateInventoryItem = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateInventoryItemRequest }) =>
      updateInventoryItem(id, data),
    onSuccess: () => invalidateInventoryAndFollowings(queryClient),
  })
}

export const useDeleteInventoryItem = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => deleteInventoryItem(id),
    onSuccess: () => invalidateInventoryAndFollowings(queryClient),
  })
}

export const useLinkInventoryToGlobal = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, request }: { id: string; request: LinkInventoryRequest }) =>
      linkInventoryToGlobal(id, request),
    onSuccess: () => invalidateInventoryAndFollowings(queryClient),
  })
}
